/**
 * Legacy MSK Reconciliation Dialog (Phase 5, 2026-02).
 *
 * Fires from the amber legacy banner on a Phase-3 region card. Lets
 * the clinician move each ``msk_impression`` / ``msk_plan`` / etc.
 * plan-text field into a chosen Clinical Summary and Plan target
 * field (append / replace / skip). On submit, the backend:
 *
 *   1. Merges each selected plan text into its Clinical Summary target
 *   2. Materialises the legacy region as a real ``mskRegions[0]`` entry
 *   3. Deletes every top-level ``msk_*`` response so the legacy shim stops
 *
 * The materialised region is then editable via the normal region card
 * flow — no "read-only" banner, no "needs reconciliation" flag.
 *
 * Endpoint contract:
 *   GET  /api/assessments/{aid}/msk-regions/legacy-msk-migration/preview
 *   POST /api/assessments/{aid}/msk-regions/legacy-msk-migration/reconcile
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { AlertTriangle, Loader2, ArrowRightLeft, XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CS_TARGET_LABELS = {
  key_findings: "Key findings",
  clinical_impression: "Clinical impression",
  impairments_limiting: "Impairments limiting goals",
  risks_precautions: "Risks / precautions",
  exercise_priorities: "Exercise priorities",
  initial_programme: "Initial programme",
  education: "Education",
  referrals: "Referrals",
  review_schedule: "Review / reassessment schedule",
};

// Best-effort default target per legacy key — the clinician still
// picks the final destination but the pre-selection nudges the
// right one for 90% of cases.
const DEFAULT_TARGET_FOR_LEGACY = {
  msk_impression: "clinical_impression",
  msk_plan: "initial_programme",
  msk_home_program: "initial_programme",
  msk_education: "education",
  msk_referrals: "referrals",
  msk_review: "review_schedule",
  msk_precautions: "risks_precautions",
  msk_goals: "clinical_impression",
};

export default function MskReconciliationDialog({
  open, onOpenChange, assessmentId, onReconciled,
}) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!open || !assessmentId) return;
    setLoading(true);
    try {
      const r = await api.get(`/assessments/${assessmentId}/msk-regions/legacy-msk-migration/preview`);
      setPreview(r.data);
      // Seed decisions per row: default to append-into-suggested-target
      // if there's text; skip otherwise.
      const seed = {};
      for (const row of r.data?.plan_text_fields || []) {
        const text = (row.text || "").trim();
        seed[row.legacy_key] = {
          target: text ? (DEFAULT_TARGET_FOR_LEGACY[row.legacy_key] || "clinical_impression") : "",
          mode: text ? "append" : "skip",
        };
      }
      setDecisions(seed);
    } catch (e) {
      toast.error("Couldn't load reconciliation preview", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, [open, assessmentId]);

  useEffect(() => { load(); }, [load]);

  const setDecision = (legacyKey, patch) => {
    setDecisions((prev) => ({
      ...prev,
      [legacyKey]: { ...(prev[legacyKey] || {}), ...patch },
    }));
  };

  const summary = useMemo(() => {
    const rows = Object.entries(decisions);
    return {
      total: rows.length,
      moving: rows.filter(([, d]) => d.mode !== "skip" && d.target).length,
      skipping: rows.filter(([, d]) => d.mode === "skip" || !d.target).length,
    };
  }, [decisions]);

  const submit = useCallback(async () => {
    if (!assessmentId) return;
    setSaving(true);
    try {
      const migrations = {};
      for (const [legacyKey, d] of Object.entries(decisions)) {
        migrations[legacyKey] = {
          target: d.mode === "skip" ? null : (d.target || null),
          mode: d.mode || "skip",
        };
      }
      const r = await api.post(
        `/assessments/${assessmentId}/msk-regions/legacy-msk-migration/reconcile`,
        { migrations },
      );
      toast.success("Reconciliation complete", {
        description: `${summary.moving} field${summary.moving === 1 ? "" : "s"} moved into Clinical Summary and Plan; region unlocked for edit.`,
      });
      onOpenChange(false);
      if (onReconciled) onReconciled(r.data);
    } catch (e) {
      toast.error("Reconciliation failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }, [assessmentId, decisions, summary.moving, onOpenChange, onReconciled]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] flex flex-col"
        data-testid="msk-reconciliation-dialog"
      >
        <DialogHeader className="border-b pb-3">
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-indigo-600" />
            Reconcile legacy MSK plan text
          </DialogTitle>
          <DialogDescription>
            Move any plan / impression / home-programme text from the old MSK module into the unified
            Clinical Summary and Plan section. Once complete, the region unlocks for edit and the
            legacy banner disappears.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-zinc-600">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading legacy fields…
            </div>
          )}

          {!loading && preview?.no_legacy && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="reconciliation-nothing-to-do">
              <div className="font-semibold flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" /> Nothing to reconcile
              </div>
              <div className="mt-1 text-xs">
                {preview.reason === "explicit_regions_exist"
                  ? "This assessment already has explicit MSK regions — legacy migration is complete."
                  : "This assessment has no legacy MSK plan text — nothing to migrate."}
              </div>
            </div>
          )}

          {!loading && preview && !preview.no_legacy && (
            <>
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-950" data-testid="reconciliation-context">
                <div className="font-semibold flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Legacy region: {preview.region_summary.body_region} · {preview.region_summary.laterality}
                </div>
                <div className="mt-1">
                  {preview.mapped_region_field_count} regional response{preview.mapped_region_field_count === 1 ? "" : "s"} (pain, ROM, observation…) will migrate automatically into a new editable region instance.
                </div>
              </div>

              {(preview.plan_text_fields || []).map((row) => {
                const d = decisions[row.legacy_key] || { target: "", mode: "skip" };
                const isSkipping = d.mode === "skip" || !d.target;
                return (
                  <div
                    key={row.legacy_key}
                    className={`rounded-lg border p-3 space-y-3 ${isSkipping ? "border-zinc-200 bg-zinc-50" : "border-indigo-200 bg-indigo-50/40"}`}
                    data-testid={`reconciliation-row-${row.legacy_key}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-semibold text-zinc-900">{row.legacy_key}</div>
                      {isSkipping ? (
                        <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-zinc-300 bg-white text-zinc-700">
                          Will be discarded
                        </span>
                      ) : (
                        <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-indigo-300 bg-indigo-100 text-indigo-900 inline-flex items-center gap-0.5">
                          <ArrowRightLeft className="h-2.5 w-2.5" />
                          {d.mode === "replace" ? "replace" : "append"} → {CS_TARGET_LABELS[d.target] || d.target}
                        </span>
                      )}
                    </div>

                    <Textarea
                      value={row.text || ""}
                      readOnly
                      rows={3}
                      className="text-sm bg-white"
                      data-testid={`reconciliation-source-text-${row.legacy_key}`}
                    />

                    <div className="grid gap-2 md:grid-cols-2">
                      <div>
                        <Label className="text-xs text-zinc-700">Move into</Label>
                        <Select
                          value={d.target || "_skip"}
                          onValueChange={(v) => setDecision(row.legacy_key, {
                            target: v === "_skip" ? "" : v,
                            mode: v === "_skip" ? "skip" : (d.mode === "skip" ? "append" : d.mode),
                          })}
                        >
                          <SelectTrigger className="h-9" data-testid={`reconciliation-target-${row.legacy_key}`}>
                            <SelectValue placeholder="Choose target…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_skip">Skip — discard this field</SelectItem>
                            {(preview.target_options || []).map((t) => (
                              <SelectItem key={t} value={t}>{CS_TARGET_LABELS[t] || t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-700">Merge mode</Label>
                        <Select
                          value={d.mode || "append"}
                          onValueChange={(v) => setDecision(row.legacy_key, { mode: v })}
                          disabled={!d.target}
                        >
                          <SelectTrigger className="h-9" data-testid={`reconciliation-mode-${row.legacy_key}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="append">Append (keep existing target text)</SelectItem>
                            <SelectItem value="replace">Replace (overwrite target)</SelectItem>
                            <SelectItem value="skip">Skip — discard</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <div className="mr-auto text-xs text-zinc-600" data-testid="reconciliation-summary">
            {!loading && preview && !preview.no_legacy && (
              <>
                <span className="font-semibold text-emerald-800">{summary.moving} moving</span>
                {" · "}
                <span className="text-zinc-600">{summary.skipping} skipping</span>
              </>
            )}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="reconciliation-cancel">
            Cancel
          </Button>
          {preview && !preview.no_legacy && (
            <Button
              onClick={submit}
              disabled={saving || loading}
              data-testid="reconciliation-submit"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ArrowRightLeft className="h-3.5 w-3.5 mr-1" />}
              Finalise reconciliation
            </Button>
          )}
          {preview?.no_legacy && (
            <Button onClick={() => onOpenChange(false)} data-testid="reconciliation-close">
              <XCircle className="h-3.5 w-3.5 mr-1" /> Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
